<script setup>
import { onMounted, reactive, ref, watch } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"
import VisorSoporte from "@/components/rrhh/VisorSoporte.vue"

const TIPOS = [
  { value: "vacaciones", label: "Vacaciones" },
  { value: "permiso", label: "Permiso" },
  { value: "incapacidad", label: "Incapacidad" },
  { value: "cita_medica", label: "Cita médica" },
  { value: "otro", label: "Otro" }
]

function labelTipo(tipo) {
  return TIPOS.find((t) => t.value === tipo)?.label || tipo
}

const ESTADO_BADGE = {
  pendiente: "bg-warning text-dark",
  aprobado: "bg-success",
  rechazado: "bg-danger",
  cancelado: "bg-secondary"
}

const ausencias = ref([])
const soporteAbierto = ref(null)
const saldoVacaciones = ref(null)
const cargando = ref(true)

async function cargarTodo() {
  cargando.value = true
  try {
    const [ausenciasRes, resumenRes] = await Promise.all([
      api.get("/rrhh/ausencias/mias"),
      api.get("/rrhh/mi-resumen")
    ])
    ausencias.value = ausenciasRes.data.ausencias
    saldoVacaciones.value = resumenRes.data.saldoVacaciones
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar tus solicitudes",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargando.value = false
  }
}

onMounted(cargarTodo)

function formatDate(value) {
  if (!value) return ""
  return new Date(value).toLocaleDateString("es-CO", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" })
}

// ─── Modal nueva solicitud ────────────────────────────────────────────────
const modalVisible = ref(false)
const guardando = ref(false)

const formVacio = () => ({
  tipo: "vacaciones",
  fechaInicio: "",
  fechaFin: "",
  horaInicio: "",
  horaFin: "",
  motivo: "",
  soporte: null
})

const form = reactive(formVacio())
const inputArchivo = ref(null)

// Vista previa de días: se pide al backend para usar las mismas reglas
// (domingos y festivos de vacaciones) que al guardar la solicitud.
const previaDias = ref(null)
let previaSeq = 0

watch(
  () => [form.tipo, form.fechaInicio, form.fechaFin, form.horaInicio, form.horaFin],
  async ([tipo, fechaInicio, fechaFin, horaInicio, horaFin]) => {
    previaDias.value = null
    if (!fechaInicio || !fechaFin) return
    const seq = ++previaSeq
    try {
      const { data } = await api.get("/rrhh/ausencias/calcular-dias", {
        params: { tipo, fechaInicio, fechaFin, horaInicio: horaInicio || undefined, horaFin: horaFin || undefined }
      })
      if (seq === previaSeq) previaDias.value = data
    } catch {
      if (seq === previaSeq) previaDias.value = null
    }
  }
)

function abrirNueva() {
  Object.assign(form, formVacio())
  modalVisible.value = true
}

function cerrarModal() {
  modalVisible.value = false
}

function onArchivoSeleccionado(e) {
  form.soporte = e.target.files[0] || null
}

async function crearSolicitud() {
  if (!form.tipo || !form.fechaInicio || !form.fechaFin) {
    Swal.fire({ icon: "warning", title: "Faltan datos", text: "Tipo, fecha de inicio y fecha de fin son obligatorios." })
    return
  }

  guardando.value = true
  try {
    const formData = new FormData()
    formData.append("tipo", form.tipo)
    formData.append("fechaInicio", form.fechaInicio)
    formData.append("fechaFin", form.fechaFin)
    if (form.horaInicio) formData.append("horaInicio", form.horaInicio)
    if (form.horaFin) formData.append("horaFin", form.horaFin)
    if (form.motivo) formData.append("motivo", form.motivo)
    if (form.soporte) formData.append("soporte", form.soporte)

    await api.post("/rrhh/ausencias", formData, { headers: { "Content-Type": "multipart/form-data" } })

    modalVisible.value = false
    if (inputArchivo.value) inputArchivo.value.value = ""
    await cargarTodo()

    Swal.fire({
      icon: "success",
      title: "Solicitud enviada",
      text: "Se notificó a tu jefe inmediato o a Recursos Humanos.",
      timer: 2200,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo enviar la solicitud",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardando.value = false
  }
}

async function cancelar(ausencia) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Cancelar solicitud?",
    text: `Se cancelará tu solicitud de ${labelTipo(ausencia.tipo).toLowerCase()}.`,
    showCancelButton: true,
    confirmButtonText: "Sí, cancelar",
    cancelButtonText: "No",
    confirmButtonColor: "#f5365c"
  })
  if (!isConfirmed) return

  try {
    await api.delete(`/rrhh/ausencias/${ausencia.id}`)
    await cargarTodo()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cancelar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="row mb-4" v-if="saldoVacaciones">
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <p class="text-sm text-muted mb-1">Días de vacaciones causados</p>
            <h5 class="mb-0">{{ saldoVacaciones.causados }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <p class="text-sm text-muted mb-1">Días disfrutados</p>
            <h5 class="mb-0">{{ saldoVacaciones.disfrutados }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <p class="text-sm text-muted mb-1">Días adelantados</p>
            <h5 class="mb-0">{{ saldoVacaciones.adelantadas }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <p class="text-sm text-muted mb-1">Saldo disponible</p>
            <h5 class="mb-0" :class="saldoVacaciones.saldo < 0 ? 'text-danger' : 'text-success'">{{ saldoVacaciones.saldo }}</h5>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 class="mb-0"><i class="ni ni-calendar-grid-58 text-primary me-2"></i>Mis solicitudes</h5>
        <button type="button" class="btn btn-primary btn-sm mb-0" @click="abrirNueva">
          <i class="ni ni-fat-add me-1"></i> Nueva solicitud
        </button>
      </div>

      <div class="card-body">
        <div v-if="cargando" class="text-center text-muted py-5">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Cargando tus solicitudes...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-striped table-bordered align-middle table-sm">
            <thead class="table-dark">
              <tr>
                <th>Tipo</th>
                <th>Desde</th>
                <th>Hasta</th>
                <th>Días</th>
                <th>Motivo</th>
                <th>Soporte</th>
                <th>Estado</th>
                <th>Comentario</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="ausencias.length === 0">
                <td colspan="9" class="text-center text-muted py-4">Aún no has hecho ninguna solicitud.</td>
              </tr>
              <tr v-for="a in ausencias" :key="a.id">
                <td>{{ labelTipo(a.tipo) }}</td>
                <td>{{ formatDate(a.fechaInicio) }}</td>
                <td>{{ formatDate(a.fechaFin) }}</td>
                <td>{{ a.diasSolicitados }}</td>
                <td>{{ a.motivo || "—" }}</td>
                <td>
                  <button
                    v-if="a.soporteDocumentoId"
                    type="button"
                    class="btn btn-sm btn-outline-info mb-0"
                    title="Ver soporte adjunto"
                    @click="soporteAbierto = a"
                  >
                    <i class="bi bi-paperclip"></i> Ver
                  </button>
                  <span v-else class="text-muted">—</span>
                </td>
                <td><span class="badge" :class="ESTADO_BADGE[a.estado]">{{ a.estado }}</span></td>
                <td>{{ a.comentarioResolucion || "—" }}</td>
                <td>
                  <button v-if="a.estado === 'pendiente'" type="button" class="btn btn-sm btn-outline-danger" @click="cancelar(a)">
                    Cancelar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- MODAL NUEVA SOLICITUD -->
    <div v-if="modalVisible" class="modal-backdrop-custom" @click.self="cerrarModal">
      <div class="modal-dialog-custom card shadow-lg">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Nueva solicitud</h6>
          <button type="button" class="btn-close" @click="cerrarModal"></button>
        </div>
        <div class="card-body">
          <form class="row g-3" @submit.prevent="crearSolicitud">
            <div class="col-md-6">
              <label class="form-label">Tipo *</label>
              <select class="form-select" v-model="form.tipo" required>
                <option v-for="t in TIPOS" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
            </div>

            <div class="col-md-3">
              <label class="form-label">Fecha inicio *</label>
              <input type="date" class="form-control" v-model="form.fechaInicio" required>
            </div>
            <div class="col-md-3">
              <label class="form-label">Fecha fin *</label>
              <input type="date" class="form-control" v-model="form.fechaFin" required>
            </div>

            <div class="col-md-3">
              <label class="form-label">Hora inicio (para citas puntuales)</label>
              <input type="time" class="form-control" v-model="form.horaInicio">
            </div>
            <div class="col-md-3">
              <label class="form-label">Hora fin</label>
              <input type="time" class="form-control" v-model="form.horaFin">
            </div>

            <div v-if="previaDias" class="col-12">
              <div class="alert alert-info mb-0 py-2">
                <template v-if="previaDias.dias === 0">Solicitud por horas: no descuenta días.</template>
                <template v-else>
                  Se descontarán <strong>{{ previaDias.dias }}</strong> día(s)
                  <span v-if="previaDias.dias !== previaDias.corridos">
                    ({{ previaDias.corridos }} corridos, sin domingos ni festivos)
                  </span>
                </template>
              </div>
            </div>

            <div class="col-12">
              <label class="form-label">Motivo</label>
              <textarea class="form-control" rows="2" v-model="form.motivo"></textarea>
            </div>

            <div class="col-12">
              <label class="form-label">Soporte adjunto (opcional, ej. incapacidad)</label>
              <input ref="inputArchivo" type="file" class="form-control" @change="onArchivoSeleccionado"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
            </div>

            <div class="col-12 d-flex justify-content-end gap-2 mt-2">
              <button type="button" class="btn btn-outline-secondary mb-0" @click="cerrarModal">Cancelar</button>
              <button type="submit" class="btn btn-primary mb-0" :disabled="guardando">
                {{ guardando ? "Enviando..." : "Enviar solicitud" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <VisorSoporte :ausencia="soporteAbierto" @cerrar="soporteAbierto = null" />
  </div>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-dialog-custom {
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
}
</style>
